import { mainApi } from '../config/apiClient';

// ════════════════════════════════════════════════════════════════════════
// Types
// ════════════════════════════════════════════════════════════════════════

export interface SecurityStats {
  rateLimitCount: number;
  ipBlockedCount: number;
  authFailedCount: number;
  exceptionCount: number;
  suspiciousCount: number;
  activeRateLimitRules: number;
  activeIpRules: number;
  topBlockedIps: { ip: string; count: number }[];
  topErrorEndpoints: { path: string; count: number }[];
  companyDistribution: { companyId: string; count: number }[];
  since: string;
}

export interface ApiSecurityLog {
  id: number;
  logType: string;
  ipAddress: string | null;
  companyId: string | null;
  userId: string | null;
  httpMethod: string | null;
  requestPath: string | null;
  statusCode: number | null;
  message: string | null;
  exceptionType: string | null;
  exceptionMessage: string | null;
  stackTrace: string | null;
  requestDurationMs: number | null;
  userAgent: string | null;
  requestBody: string | null;
  apiName: string | null;
  createdAt: string;
}

export interface RateLimitRule {
  id: number;
  ruleName: string;
  scope: string;
  companyId: string | null;
  endpointPattern: string | null;
  maxRequests: number;
  windowSeconds: number;
  burstLimit: number | null;
  isActive: boolean;
  priority: number;
  createdAt: string;
  updatedAt: string | null;
}

export interface IpAccessRule {
  id: number;
  ipAddress: string;
  ipRangeEnd: string | null;
  ruleType: string;
  scope: string;
  companyId: string | null;
  reason: string | null;
  isActive: boolean;
  isPermanent: boolean;
  expiresAt: string | null;
  blockedRequestCount: number;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string | null;
}

export interface LogsResponse {
  data: ApiSecurityLog[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface LogFilters {
  logType?: string;
  ip?: string;
  companyId?: string;
  apiName?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

export interface FileLogFile {
  instance: string;
  fileName: string;
  fullPath: string;
  sizeBytes: number;
  sizeKB: number;
  lastWrite: string;
}

export interface FileLogListResponse {
  success: boolean;
  apiName?: string;
  rootPath?: string;
  files: FileLogFile[];
  message?: string;
}

export interface FileLogTailResponse {
  success: boolean;
  file?: string;
  fullPath?: string;
  sizeBytes?: number;
  lastWrite?: string;
  lineCount?: number;
  lines: string[];
  message?: string;
}

// ════════════════════════════════════════════════════════════════════════
// API Calls
// ════════════════════════════════════════════════════════════════════════

export const getSecurityStats = async (): Promise<SecurityStats> => {
  const { data } = await mainApi.get('/api/security/stats');
  return data;
};

export const getSecurityLogs = async (filters: LogFilters): Promise<LogsResponse> => {
  const params = new URLSearchParams();
  if (filters.logType) params.set('logType', filters.logType);
  if (filters.ip) params.set('ip', filters.ip);
  if (filters.companyId) params.set('companyId', filters.companyId);
  if (filters.apiName) params.set('apiName', filters.apiName);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  const { data } = await mainApi.get(`/api/security/logs?${params.toString()}`);
  return data;
};

export const getRecentLogs = async (count = 50): Promise<ApiSecurityLog[]> => {
  const { data } = await mainApi.get(`/api/security/logs/recent?count=${count}`);
  return data;
};

export const getLogDetail = async (id: number): Promise<ApiSecurityLog> => {
  const { data } = await mainApi.get(`/api/security/logs/${id}`);
  return data;
};

export const getApiNames = async (): Promise<string[]> => {
  const { data } = await mainApi.get('/api/security/api-names');
  return data;
};

// ════════════════════════════════════════════════════════════════════════
// FILE LOGS (sunucudaki Serilog .log dosyalari)
// ════════════════════════════════════════════════════════════════════════

export const listFileLogs = async (apiName: string): Promise<FileLogListResponse> => {
  const { data } = await mainApi.get(`/api/security/file-logs/list?apiName=${encodeURIComponent(apiName)}`);
  return data;
};

export const tailFileLog = async (params: {
  apiName: string;
  instance?: 'instance_a' | 'instance_b';
  fileName?: string;
  lines?: number;
  grep?: string;
}): Promise<FileLogTailResponse> => {
  const qs = new URLSearchParams();
  qs.set('apiName', params.apiName);
  if (params.instance) qs.set('instance', params.instance);
  if (params.fileName) qs.set('fileName', params.fileName);
  if (params.lines) qs.set('lines', String(params.lines));
  if (params.grep) qs.set('grep', params.grep);
  const { data } = await mainApi.get(`/api/security/file-logs/tail?${qs.toString()}`);
  return data;
};

export const getRateLimitRules = async (): Promise<RateLimitRule[]> => {
  const { data } = await mainApi.get('/api/security/rate-limit-rules');
  return data;
};

export const createRateLimitRule = async (rule: Omit<RateLimitRule, 'id' | 'createdAt' | 'updatedAt'>): Promise<{ success: boolean; rule: RateLimitRule }> => {
  const { data } = await mainApi.post('/api/security/rate-limit-rules', rule);
  return data;
};

export const toggleRateLimitRule = async (id: number): Promise<{ success: boolean; isActive: boolean }> => {
  const { data } = await mainApi.post(`/api/security/rate-limit-rules/${id}/toggle`);
  return data;
};

export const deleteRateLimitRule = async (id: number): Promise<{ success: boolean }> => {
  const { data } = await mainApi.post(`/api/security/rate-limit-rules/${id}/delete`);
  return data;
};

export const getIpAccessRules = async (): Promise<IpAccessRule[]> => {
  const { data } = await mainApi.get('/api/security/ip-access-rules');
  return data;
};

export const createIpAccessRule = async (rule: Omit<IpAccessRule, 'id' | 'createdAt' | 'updatedAt' | 'blockedRequestCount' | 'createdBy'>): Promise<{ success: boolean; rule: IpAccessRule }> => {
  const { data } = await mainApi.post('/api/security/ip-access-rules', rule);
  return data;
};

export const toggleIpAccessRule = async (id: number): Promise<{ success: boolean; isActive: boolean }> => {
  const { data } = await mainApi.post(`/api/security/ip-access-rules/${id}/toggle`);
  return data;
};

export const deleteIpAccessRule = async (id: number): Promise<{ success: boolean }> => {
  const { data } = await mainApi.post(`/api/security/ip-access-rules/${id}/delete`);
  return data;
};
